import { useTranslation } from 'react-i18next';
import type * as echarts from 'echarts/core';

/**
 * Per-chart PNG export. Slot into a chart widget's `titleBarExtra`.
 *
 * Uses ECharts' built-in `getDataURL` at 2× pixel ratio so the PNG looks
 * sharp on retina displays, with the current theme background baked in
 * (a transparent PNG looks awful when pasted into a dark slide deck).
 *
 * `html2canvas` is explicitly forbidden by the brief (§14) — this is the
 * approved path.
 */
type ChartExportButtonProps = {
  title: string;
  getInstance: () => echarts.ECharts | null;
};

export function ChartExportButton({
  title,
  getInstance,
}: ChartExportButtonProps): JSX.Element {
  const { t } = useTranslation();

  function handleClick(): void {
    const inst = getInstance();
    if (!inst) {
      window.alert(t('export.png.notReady'));
      return;
    }
    const bg = readThemeBackground();
    const dataUrl = inst.getDataURL({
      type: 'png',
      pixelRatio: 2,
      backgroundColor: bg,
    });
    triggerDownload(dataUrl, title);
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label={t('export.png.ariaDownload', { title })}
      title={t('export.png.tooltip')}
      className="shrink-0 flex items-center justify-center w-6 h-6 rounded hover:opacity-80"
      style={{ color: 'var(--color-muted)' }}
    >
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden>
        <path
          d="M8 2 V10 M5 7 L8 10 L11 7"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M3 12 V13 A1 1 0 0 0 4 14 H12 A1 1 0 0 0 13 13 V12"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    </button>
  );
}

/** Read `--color-bg` from the document root. Falls back to white. */
function readThemeBackground(): string {
  if (typeof window === 'undefined') return '#ffffff';
  const v = getComputedStyle(document.documentElement)
    .getPropertyValue('--color-bg')
    .trim();
  return v.length > 0 ? v : '#ffffff';
}

function triggerDownload(dataUrl: string, title: string): void {
  const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
  const safe = title.replace(/[^\p{L}\p{N}_-]+/gu, '_').slice(0, 60) || 'chart';
  const a = document.createElement('a');
  a.href = dataUrl;
  a.download = `${safe}-${stamp}.png`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}
